'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { pick } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import text from 'I18n/utils/unescapedText';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UIHelpLink from 'UIComponents/tooltip/UIHelpLink';
import UILink from 'UIComponents/link/UILink';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import UIAlert from 'UIComponents/alert/UIAlert';
import Small from 'UIComponents/elements/Small';
import Tool from 'DesignComponents/tool/Tool';
import ToolBreadcrumbs from 'DesignComponents/tool/ToolBreadcrumbs';
import ToolExpandableSection from 'DesignComponents/tool/ToolExpandableSection';
import ToolSection from 'DesignComponents/tool/ToolSection';
import ToolSections from 'DesignComponents/tool/ToolSections';
import StyleGroupEditContainer from 'DesignEditorsUI/containers/customWidgetEditor/StyleGroupEdit';
import { GLOBAL_CONTENT, STYLE_FIELDS, FIELDS, EDITOR_OPTIONS, LINKED_FILES, TAGS, SNIPPET, TRANSLATIONS } from 'DesignData/constants/AccordionKeys';
import FilterTags from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/FilterTags';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import LinkedFiles from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/LinkedFiles';
import EditorOptions from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/EditorOptions';
import Translations from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/Translations';
import FieldsTable from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/fieldsTable/FieldsTable';
import CreateFieldDropdown from 'DesignEditorsUI/components/customWidgetEditor/sidebar/CreateFieldDropdown';
import FieldGrouping from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FieldGrouping';
import CopyFieldsJsonButton from 'DesignEditorsUI/components/customWidgetEditor/sidebar/CopyFieldsJsonButton';
import ModuleSnippet from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/ModuleSnippet';
import GlobalAndSmartContent from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/GlobalAndSmartContent';
import GlobalContentEditButton from 'DesignEditorsUI/components/shared/globalContent/GlobalContentEditButton';
import ErrorIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/ErrorIcon';
import WarningIcon from 'DesignEditorsUI/components/customWidgetEditor/sidebar/WarningIcon';
import { isChild } from 'DesignData/helpers/customWidgetFieldsHelper';
import NameLabel from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/NameLabel';
import WidgetIcon from 'ContentComponents/cmv2/WidgetIcon';
import { STYLE } from 'ContentUtils/constants/CustomWidgetFieldGroupTabTypes';
import { getIsOpen } from 'DesignData/helpers/accordionHelpers';
import withScrollDropTargets from 'DesignComponents/dnd/withScrollDropTargets';
import { CUSTOM_WIDGET_CHOICE_FIELD_OPTION, CUSTOM_WIDGET_FIELD_TABLE_ROW } from 'DesignData/enums/DragDropItemTypes';
import EmailV3RenderingToggle from './EmailV3RenderingToggle';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.${key}`;

const mainSidebarDropTypes = [CUSTOM_WIDGET_FIELD_TABLE_ROW, CUSTOM_WIDGET_CHOICE_FIELD_OPTION];
const ScrollableUISection = withScrollDropTargets(mainSidebarDropTypes)(UIScrollContainer);
/* Returns all of a field's errors in one array
Example:
const fieldErrors = { name: [ {type: "ERROR1"} ], default: [ {type: "ERROR2"}, {type: "ERROR3"} ]};
combineErrors(fieldErrors) => [ {type: "ERROR1"}, {type: "ERROR2"}, {type: "ERROR3"} ]
*/

const combineErrors = fieldErrors => {
  return Object.keys(fieldErrors).reduce((acc, key) => {
    return acc.concat(fieldErrors[key] || []);
  }, []);
};

export default class MainSidebar extends PureComponent {
  constructor(...args) {
    super(...args);

    this.onFieldCreate = fieldType => {
      const {
        createField,
        customWidgetId,
        fieldCreateOptions
      } = this.props;
      const newField = new CustomWidgetField({
        type: fieldType,
        isNew: true,
        fieldCreateOptions
      });
      createField(customWidgetId, newField);
    };

    this.onStyleFieldCreate = fieldType => {
      const {
        styleFields,
        createField,
        customWidgetId,
        fieldCreateOptions
      } = this.props;
      const newField = new CustomWidgetField({
        type: fieldType,
        isNew: true,
        fieldCreateOptions
      });
      createField(customWidgetId, newField, styleFields.id);
    };

    this.onGroupClick = fieldType => {
      const {
        customWidgetId,
        toggleFieldGrouping
      } = this.props;
      toggleFieldGrouping(customWidgetId, fieldType);
    };

    this.onTagsChange = (contentTags, tags) => {
      this.props.updateCustomWidget({
        contentTags,
        tags
      });
    };

    this.onCategoriesChange = categories => {
      this.props.updateCustomWidget({
        categories
      });
    };

    this.onOpenChange = key => {
      return evt => {
        this.props.saveAccordionState(key, evt.target.value);
      };
    };

    this.onAddStyleFieldGroup = () => {
      const {
        createFieldGroup,
        customWidgetId
      } = this.props;
      const fieldGroup = new CustomWidgetField({
        type: 'group',
        isNew: false,
        name: 'style',
        label: 'Style',
        tab: 'STYLE'
      });
      createFieldGroup(customWidgetId, fieldGroup);
    };

    this.sortFieldErrors = errors => {
      const {
        styleFields
      } = this.props;
      const content = {};
      const style = {};
      Object.keys(errors).forEach(fieldId => {
        const combinedFieldErrors = combineErrors(errors[fieldId]);

        if (styleFields && isChild(styleFields, fieldId)) {
          style[fieldId] = combinedFieldErrors;
        } else {
          content[fieldId] = combinedFieldErrors;
        }
      });
      return {
        content,
        style
      };
    };
  }

  getIsOpen(key) {
    return getIsOpen(this.props.accordionStates, key);
  }

  get editorOptionsErrors() {
    const {
      moduleErrors
    } = this.props;
    return pick(moduleErrors, ['inline_help_text']);
  }

  renderBreadcrumbs() {
    const {
      customWidgetLabel
    } = this.props;
    const {
      customWidgetIcon,
      isReadOnly
    } = this.props;

    const breadcrumbsIcon = /*#__PURE__*/_jsx(WidgetIcon, {
      alignment: "text-bottom",
      size: 14,
      icon: customWidgetIcon
    });

    const topLevelLabel = customWidgetLabel ? /*#__PURE__*/_jsx("span", {
      children: customWidgetLabel
    }) : null;
    return /*#__PURE__*/_jsx(ToolBreadcrumbs, {
      className: "field-edit-breadcrumbs",
      isReadOnly: isReadOnly,
      rootIcon: breadcrumbsIcon,
      rootLabel: topLevelLabel
    });
  }

  renderNameLabel() {
    return /*#__PURE__*/_jsx(NameLabel, {
      customWidgetId: this.props.customWidgetId,
      isReadOnly: this.props.isReadOnly,
      revisionId: this.props.revisionId,
      updateCustomWidget: this.props.updateCustomWidget
    });
  }

  renderEmailV3RenderingToggle() {
    return /*#__PURE__*/_jsx(EmailV3RenderingToggle, {
      customWidgetId: this.props.customWidgetId,
      isReadOnly: this.props.isReadOnly,
      updateCustomWidget: this.props.updateCustomWidget
    });
  }

  renderFields() {
    const {
      fields,
      customWidgetId,
      isReadOnly
    } = this.props;
    return /*#__PURE__*/_jsx(FieldsTable, {
      customWidgetId: customWidgetId,
      fields: fields,
      isReadOnly: isReadOnly
    });
  }

  renderStyleFields() {
    const {
      styleFields,
      customWidgetId,
      isReadOnly
    } = this.props;
    return /*#__PURE__*/_jsx(FieldsTable, {
      customWidgetId: customWidgetId,
      fields: styleFields && styleFields.children || [],
      styleFields: styleFields,
      isStyleTab: true,
      fieldName: styleFields && styleFields.name,
      isReadOnly: isReadOnly,
      createStyleFieldGroup: this.onAddStyleFieldGroup
    });
  }

  renderStyleTabEditControls() {
    const {
      styleFields,
      customWidgetId,
      isReadOnly,
      revisionId
    } = this.props;
    return /*#__PURE__*/_jsx(StyleGroupEditContainer, {
      customWidgetId: customWidgetId,
      revisionId: revisionId,
      field: styleFields,
      isReadOnly: isReadOnly
    });
  }

  renderStyleFieldsAlert() {
    const {
      isEmailOrAnyType
    } = this.props;
    if (!isEmailOrAnyType) return null;
    return /*#__PURE__*/_jsx(UIAlert, {
      className: "m-bottom-4",
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('emailStyleFieldsWarning')
      })
    });
  }

  renderFieldsDropdown() {
    const {
      isReadOnly,
      isFieldGroupingActive,
      isV1,
      customWidgetId,
      fields
    } = this.props;

    if (isFieldGroupingActive) {
      return null;
    }

    if (isReadOnly) {
      return /*#__PURE__*/_jsx(CopyFieldsJsonButton, {
        fields: fields
      });
    }

    if (isV1) {
      return /*#__PURE__*/_jsx(CreateFieldDropdown, {
        customWidgetId: customWidgetId,
        onFieldCreate: this.onFieldCreate
      });
    }

    const actionDropdownText = /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('actionButtonLabel')
      })
    });

    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(CreateFieldDropdown, {
        customWidgetId: customWidgetId,
        onFieldCreate: this.onFieldCreate
      }), /*#__PURE__*/_jsx(UIDropdown, {
        buttonClassName: "m-left-0",
        buttonUse: "transparent",
        buttonText: actionDropdownText,
        menuWidth: 150,
        children: /*#__PURE__*/_jsxs(UIList, {
          children: [/*#__PURE__*/_jsx(CopyFieldsJsonButton, {
            fields: fields
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              this.onGroupClick('CONTENT');
            },
            use: "link",
            children: /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('fields.groupLabel')
              })
            })
          })]
        })
      })]
    });
  }

  renderStyleFieldsDropdown() {
    const {
      isReadOnly,
      isFieldGroupingActive,
      customWidgetId,
      styleFields
    } = this.props;

    if (isFieldGroupingActive) {
      return null;
    }

    if (isReadOnly) {
      return /*#__PURE__*/_jsx(CopyFieldsJsonButton, {
        fields: styleFields
      });
    }

    const actionDropdownText = /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('actionButtonLabel')
      })
    });

    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(CreateFieldDropdown, {
        customWidgetId: customWidgetId,
        onFieldCreate: this.onStyleFieldCreate,
        disabled: !styleFields,
        isStyleTab: true
      }), /*#__PURE__*/_jsx(UIDropdown, {
        buttonClassName: "m-left-0",
        buttonUse: "transparent",
        buttonText: actionDropdownText,
        menuWidth: 150,
        children: /*#__PURE__*/_jsxs(UIList, {
          children: [/*#__PURE__*/_jsx(CopyFieldsJsonButton, {
            fields: styleFields
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              this.onGroupClick(STYLE);
            },
            disabled: !styleFields,
            use: "link",
            children: /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('fields.groupLabel')
              })
            })
          })]
        })
      })]
    });
  }

  renderGlobalAndSmartContentAccordion() {
    const {
      customWidgetPath,
      isGlobal,
      moduleId
    } = this.props;
    if (!(isGlobal && moduleId)) return null;
    return /*#__PURE__*/_jsx(ToolExpandableSection, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`accordionLabels.globalAndSmartContent`)
      }),
      onOpenChange: this.onOpenChange(GLOBAL_CONTENT),
      open: this.getIsOpen(GLOBAL_CONTENT),
      children: this.props.hasGlobalContentEditorAccess ? /*#__PURE__*/_jsx(GlobalContentEditButton, {
        contentPath: customWidgetPath,
        globalContentType: "module"
      }) : /*#__PURE__*/_jsx(GlobalAndSmartContent, {
        customWidgetId: this.props.customWidgetId
      })
    }, "global-content");
  }

  renderLinkedFilesHelpLink() {
    const {
      isEmailOrAnyType
    } = this.props;

    const header = /*#__PURE__*/_jsx("h4", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('linkedFiles.helpLink.header')
      })
    });

    const body = /*#__PURE__*/_jsxs("p", {
      children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: i18nKey('linkedFiles.helpLink.body')
      }), isEmailOrAnyType ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: i18nKey('linkedFiles.helpLink.emailWarning')
      }) : null]
    });

    return /*#__PURE__*/_jsx(UIHelpLink, {
      closeOnTargetLeave: true,
      content: {
        header,
        body
      },
      placement: "bottom left",
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('linkedFiles.helpLink.text')
        })
      })
    });
  }

  renderFieldTranslationsHelpLink() {
    const header = /*#__PURE__*/_jsx("h4", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('translations.helpLink.header')
      })
    });

    const body = /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('translations.helpLink.body_jsx'),
        options: {
          external: true,
          href: 'https://knowledge.hubspot.com/articles/kcs_article/cos-general/create-translations-of-your-modules'
        },
        elements: {
          Link: UILink
        }
      })
    });

    return /*#__PURE__*/_jsx(UIHelpLink, {
      closeOnTargetLeave: true,
      content: {
        header,
        body
      },
      placement: "bottom left",
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('translations.helpLink.text')
        })
      })
    });
  }

  renderFieldTranslations() {
    const {
      customWidgetId,
      customWidgetLabel,
      revisionId,
      isReadOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolExpandableSection, {
      actions: this.renderFieldTranslationsHelpLink(),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('accordionLabels.translations')
      }),
      onOpenChange: this.onOpenChange(TRANSLATIONS),
      open: this.getIsOpen([TRANSLATIONS]),
      children: /*#__PURE__*/_jsx(Translations, {
        customWidgetId: customWidgetId,
        customWidgetLabel: customWidgetLabel,
        revisionId: revisionId,
        isReadOnly: isReadOnly
      })
    }, "field-translations");
  }

  renderAccordions() {
    const {
      customWidgetId,
      isFieldGroupingActive,
      fieldType,
      updateCustomWidget,
      isReadOnly,
      assets,
      isModuleCategoriesUngated,
      isModuleStyleFieldsUngated,
      revisionId,
      isV1,
      customWidgetInlineHelpText,
      fieldsErrors,
      fieldsWarnings
    } = this.props;
    const {
      content: contentFieldsErrors,
      style: styleFieldsErrors
    } = this.sortFieldErrors(fieldsErrors);
    const {
      content: contentFieldsWarnings,
      style: styleFieldsWarnings
    } = this.sortFieldErrors(fieldsWarnings);

    const fieldsLabel = /*#__PURE__*/_jsxs(UIFlex, {
      align: "end",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: "m-right-2",
        fieldErrors: contentFieldsErrors,
        i18nKey: i18nKey('accordionLabels.hasError')
      }), /*#__PURE__*/_jsx(WarningIcon, {
        className: "m-right-2",
        fieldWarnings: contentFieldsWarnings,
        i18nKey: i18nKey('accordionLabels.hasWarning')
      }), /*#__PURE__*/_jsx(UITruncateString, {
        matchContentWidth: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('accordionLabels.fields')
        })
      })]
    });

    const styleFieldsLabel = /*#__PURE__*/_jsxs(UIFlex, {
      align: "end",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: "m-right-2",
        fieldErrors: styleFieldsErrors,
        i18nKey: i18nKey('accordionLabels.hasError')
      }), /*#__PURE__*/_jsx(WarningIcon, {
        className: "m-right-2",
        fieldWarnings: styleFieldsWarnings,
        i18nKey: i18nKey('accordionLabels.hasWarning')
      }), /*#__PURE__*/_jsx(UITruncateString, {
        matchContentWidth: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('accordionLabels.styleFields')
        })
      })]
    });

    const editorOptionsLabel = /*#__PURE__*/_jsxs(UIFlex, {
      align: "end",
      children: [/*#__PURE__*/_jsx(ErrorIcon, {
        className: "m-right-2",
        fieldErrors: this.editorOptionsErrors,
        i18nKey: i18nKey('accordionLabels.hasError')
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('accordionLabels.editorOptions')
      })]
    });

    const linkedFilesLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('accordionLabels.linkedFiles'),
      options: {
        fileCount: assets.otherAssets.length + assets.cssAssets.length + assets.jsAssets.length
      }
    });

    const tagsLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('accordionLabels.tags')
    });

    if (isFieldGroupingActive) {
      return fieldType === STYLE ? this.renderStyleFields() : this.renderFields();
    }

    if (isV1) {
      return [/*#__PURE__*/_jsx(ToolExpandableSection, {
        actions: this.renderFieldsDropdown(),
        defaultOpen: true,
        titleText: fieldsLabel,
        onOpenChange: this.onOpenChange(FIELDS),
        open: this.getIsOpen(FIELDS),
        children: this.renderFields()
      }, "fields")];
    }

    return [this.renderGlobalAndSmartContentAccordion(), /*#__PURE__*/_jsx(ToolExpandableSection, {
      actions: this.renderFieldsDropdown(),
      defaultOpen: !this.props.isGlobal,
      titleText: fieldsLabel,
      onOpenChange: this.onOpenChange(FIELDS),
      open: this.getIsOpen(FIELDS),
      children: this.renderFields()
    }, "fields"), isModuleStyleFieldsUngated && /*#__PURE__*/_jsxs(ToolExpandableSection, {
      fieldType: "style",
      actions: this.renderStyleFieldsDropdown(),
      defaultOpen: !this.props.isGlobal,
      titleText: styleFieldsLabel,
      onOpenChange: this.onOpenChange(STYLE_FIELDS),
      open: this.getIsOpen(STYLE_FIELDS),
      children: [this.renderStyleFieldsAlert(), this.renderStyleTabEditControls(), this.renderStyleFields()]
    }, "style-fields"), /*#__PURE__*/_jsx(ToolExpandableSection, {
      titleText: editorOptionsLabel,
      onOpenChange: this.onOpenChange(EDITOR_OPTIONS),
      open: this.getIsOpen(EDITOR_OPTIONS),
      children: /*#__PURE__*/_jsx(EditorOptions, {
        customWidgetId: customWidgetId,
        isReadOnly: isReadOnly,
        updateCustomWidget: updateCustomWidget,
        revisionId: revisionId,
        moduleErrors: this.editorOptionsErrors,
        inlineHelpText: customWidgetInlineHelpText
      })
    }, "editor-options"), /*#__PURE__*/_jsx(ToolExpandableSection, {
      actions: this.renderLinkedFilesHelpLink(),
      titleText: linkedFilesLabel,
      onOpenChange: this.onOpenChange(LINKED_FILES),
      open: this.getIsOpen(LINKED_FILES),
      children: /*#__PURE__*/_jsx(LinkedFiles, {
        customWidgetId: customWidgetId,
        isReadOnly: isReadOnly,
        updateCustomWidget: updateCustomWidget,
        revisionId: revisionId
      })
    }, "linked-files"), /*#__PURE__*/_jsx(ToolExpandableSection, {
      titleText: tagsLabel,
      onOpenChange: this.onOpenChange([TAGS]),
      open: this.getIsOpen(TAGS),
      children: /*#__PURE__*/_jsx(FilterTags, {
        customWidgetCategories: this.props.customWidgetCategories,
        customWidgetTags: this.props.customWidgetTags,
        isModuleCategoriesUngated: isModuleCategoriesUngated,
        isReadOnly: isReadOnly,
        onCategoriesChange: this.onCategoriesChange,
        onTagsChange: this.onTagsChange
      })
    }, "tags"), this.renderFieldTranslations(), /*#__PURE__*/_jsx(ToolExpandableSection, {
      titleText: text(i18nKey('accordionLabels.moduleSnippet')),
      onOpenChange: this.onOpenChange(SNIPPET),
      open: this.getIsOpen(SNIPPET),
      children: /*#__PURE__*/_jsx(ModuleSnippet, {
        customWidgetId: this.props.customWidgetId,
        customWidgetLabel: this.props.customWidgetLabel,
        customWidgetPath: this.props.customWidgetPath,
        moduleId: this.props.moduleId
      })
    }, "snippet")];
  }

  renderGroupControls() {
    const {
      customWidgetId,
      isFieldGroupingActive,
      isV1
    } = this.props;

    if (!isFieldGroupingActive || isV1) {
      return null;
    }

    return /*#__PURE__*/_jsx(ToolSection, {
      flush: true,
      children: /*#__PURE__*/_jsx(FieldGrouping, {
        customWidgetId: customWidgetId
      })
    });
  }

  render() {
    return /*#__PURE__*/_jsx(Tool, {
      className: "main-sidebar-container",
      breadcrumbsContent: this.renderBreadcrumbs(),
      contentComponent: ScrollableUISection,
      children: /*#__PURE__*/_jsxs(ToolSections, {
        className: "main-sidebar-controls",
        children: [this.renderNameLabel(), this.renderEmailV3RenderingToggle(), this.renderGroupControls(), this.renderAccordions()]
      })
    });
  }

}
MainSidebar.propTypes = {
  assets: PropTypes.object.isRequired,
  createField: PropTypes.func.isRequired,
  createFieldGroup: PropTypes.func.isRequired,
  isGlobal: PropTypes.bool.isRequired,
  customWidgetLabel: PropTypes.string.isRequired,
  customWidgetTags: PropTypes.array.isRequired,
  customWidgetCategories: PropTypes.array.isRequired,
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  fieldCreateOptions: PropTypes.object.isRequired,
  fieldsErrors: PropTypes.object.isRequired,
  fieldsWarnings: PropTypes.object.isRequired,
  moduleErrors: PropTypes.object.isRequired,
  moduleId: PropTypes.number,
  isFieldGroupingActive: PropTypes.bool.isRequired,
  fields: PropTypes.arrayOf(PropTypes.instanceOf(CustomWidgetField)).isRequired,
  styleFields: PropTypes.instanceOf(CustomWidgetField),
  isModuleCategoriesUngated: PropTypes.bool,
  isModuleStyleFieldsUngated: PropTypes.bool,
  isEmailOrAnyType: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  toggleFieldGrouping: PropTypes.func.isRequired,
  fieldType: PropTypes.string,
  updateCustomWidget: PropTypes.func.isRequired,
  saveAccordionState: PropTypes.func.isRequired,
  accordionStates: PropTypes.object.isRequired,
  revisionId: PropTypes.number,
  customWidgetIcon: PropTypes.string,
  isV1: PropTypes.bool.isRequired,
  customWidgetPath: PropTypes.string.isRequired,
  hasGlobalContentEditorAccess: PropTypes.bool,
  customWidgetInlineHelpText: PropTypes.string
};